import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Shield, AlertTriangle, Lock, Download, RefreshCcw, Database, Trash2, FileWarning, FolderCog, CheckCircle2, XCircle, Save, Cloud, CloudOff, FolderOpen, ChevronLeft, Upload, HardDrive, Clock, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { fileSystemAccessSupported, isInIframe, pickLocalFolder, getStoredFolder, clearStoredFolder, setAutoSave, autoSaveLatestBackup, saveBlobToFolder } from "@/lib/localBackup";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";
const fmtSize = (n) => {
  if (!n) return "0";
  const kb = n / 1024; if (kb < 1024) return kb.toFixed(1) + " KB";
  const mb = kb / 1024; if (mb < 1024) return mb.toFixed(1) + " MB";
  return (mb / 1024).toFixed(2) + " GB";
};

function SummaryCard({ label, value, icon: Icon, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    rose: "bg-rose-50 border-rose-300 text-rose-700",
    amber: "bg-amber-50 border-amber-300 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-700",
    sky: "bg-sky-50 border-sky-300 text-sky-700",
  };
  return (
    <Card className={`p-4 border ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold opacity-80"><Icon className="w-4 h-4"/>{label}</div>
      <div className="font-heading text-3xl font-black mt-1">{value ?? "—"}</div>
    </Card>
  );
}

export default function SecurityPanel() {
  const [summary, setSummary] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [pix, setPix] = useState({ items: [], suspects: [] });
  const [backups, setBackups] = useState({ files: [], log: [], retention_days: 30 });
  const [tab, setTab] = useState("summary");
  const [attemptsFilter, setAttemptsFilter] = useState("failed");
  const [restoring, setRestoring] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busyBackup, setBusyBackup] = useState(false);
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ backup_path: "", retention_days: 30 });
  const [busyConfig, setBusyConfig] = useState(false);
  const [schedTimes, setSchedTimes] = useState([]);
  const [tzOffset, setTzOffset] = useState(-3);
  const [newTime, setNewTime] = useState("04:00");
  const [drive, setDrive] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerParent, setPickerParent] = useState("root");
  const [pickerFolders, setPickerFolders] = useState([]);
  const [pickerBreadcrumbs, setPickerBreadcrumbs] = useState([{ id: "root", name: "Meu Drive" }]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [busyUpload, setBusyUpload] = useState(false);
  const [localFolderName, setLocalFolderName] = useState(null);
  const [localAutoSave, setLocalAutoSave] = useState(false);
  const [localLastSaved, setLocalLastSaved] = useState(null);
  const [localHandleRef, setLocalHandleRef] = useState(null);
  const pollRef = useRef(null);

  const fetchDrive = async () => {
    try { const { data } = await api.get("/admin/drive/status"); setDrive(data); } catch {}
  };

  const fetchConfig = async () => {
    try {
      const { data } = await api.get("/admin/backup/config");
      setConfig(data);
      setConfigForm({ backup_path: data.backup_path || "", retention_days: data.retention_days || 30 });
      setSchedTimes(Array.isArray(data.schedule_times) ? data.schedule_times : []);
      setTzOffset(typeof data.timezone_offset === "number" ? data.timezone_offset : -3);
    } catch (e) { /* ignora */ }
  };

  const saveConfig = async () => {
    if (!configForm.backup_path?.trim()) return toast.error("Informe o caminho");
    setBusyConfig(true);
    try {
      await api.put("/admin/backup/config", { ...configForm, schedule_times: schedTimes, timezone_offset: tzOffset });
      toast.success("Configuração salva — próximos backups usarão esse caminho e horários");
      await fetchConfig(); await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar configuração"); }
    finally { setBusyConfig(false); }
  };
  const addTime = () => {
    if (!/^\d{2}:\d{2}$/.test(newTime)) return toast.error("Formato HH:MM");
    const next = Array.from(new Set([...schedTimes, newTime])).sort();
    setSchedTimes(next); setNewTime("04:00");
  };
  const removeTime = (t) => setSchedTimes(schedTimes.filter(x => x !== t));

  // ===== Pasta no computador =====
  const refreshLocal = async () => {
    try {
      const s = await getStoredFolder();
      setLocalFolderName(s.name);
      setLocalAutoSave(!!s.auto_save);
      setLocalLastSaved(s.last_saved);
      setLocalHandleRef(s.handle);
    } catch {}
  };
  useEffect(() => { refreshLocal(); }, []);
  const chooseLocalFolder = async () => {
    try {
      const name = await pickLocalFolder();
      toast.success(`Pasta escolhida: ${name}`);
      await refreshLocal();
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(e.message || "Falha ao escolher pasta");
    }
  };
  const forgetLocal = async () => {
    if (!confirm("Esquecer pasta local? Backups vão deixar de gravar no seu computador automaticamente.")) return;
    await clearStoredFolder(); await refreshLocal(); toast.success("Pasta local esquecida");
  };
  const toggleAutoSave = async (v) => { await setAutoSave(v); setLocalAutoSave(v); if (v) toast.success("Auto-save ligado"); };
  const saveNowLocal = async () => {
    const s = await getStoredFolder();
    if (!s.handle) return toast.error("Escolha uma pasta primeiro");
    try {
      const r = await autoSaveLatestBackup(api);
      if (r.ok && !r.skipped) toast.success(`Salvo em ${s.name}/${r.filename}`);
      else if (r.skipped) toast.info(r.reason === "já salvo" ? "Já está atualizado" : "Nada a salvar");
      else toast.error(r.error || "Falha ao salvar");
      await refreshLocal();
    } catch (e) { toast.error(e.message || "Falha"); }
  };
  // Auto-save: a cada 60s se ligado, verifica se tem backup novo e grava
  useEffect(() => {
    if (!localAutoSave || !localHandleRef) return;
    const tick = async () => {
      try { const r = await autoSaveLatestBackup(api); if (r.ok && !r.skipped) { toast.success(`Backup gravado: ${r.filename}`); await refreshLocal(); } } catch {}
    };
    tick(); const id = setInterval(tick, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAutoSave, localHandleRef]);

  // ===== Restauração =====
  const [restoreHistory, setRestoreHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef(null);
  const fetchRestoreHistory = async () => {
    try { const { data } = await api.get("/admin/backup/restores"); setRestoreHistory(data || []); } catch {}
  };
  useEffect(() => { if (tab === "restore") fetchRestoreHistory(); }, [tab]);
  const onPickUpload = () => uploadRef.current?.click();
  const onUploadFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.name.endsWith(".tar.enc")) return toast.error("Selecione um arquivo .tar.enc");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/backup/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Backup disponível para restaurar: ${data.filename}`);
      await fetchAll(); await fetchRestoreHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha no upload"); }
    finally { setUploading(false); }
  };

  const connectDrive = async () => {
    try {
      const { data } = await api.get("/admin/drive/connect");
      const w = 520, h = 640;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      window.open(data.authorization_url, "driveOAuth", `width=${w},height=${h},left=${left},top=${top}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar OAuth"); }
  };
  const disconnectDrive = async () => {
    if (!confirm("Desconectar Google Drive? Backups diários voltarão a ficar só localmente.")) return;
    try { await api.post("/admin/drive/disconnect"); toast.success("Desconectado"); await fetchDrive(); }
    catch { toast.error("Falha"); }
  };
  const openPicker = async () => {
    setPickerBreadcrumbs([{ id: "root", name: "Meu Drive" }]);
    setPickerParent("root"); setPickerOpen(true);
    await loadFolders("root");
  };
  const loadFolders = async (parent) => {
    setPickerLoading(true);
    try {
      const { data } = await api.get(`/admin/drive/folders?parent=${encodeURIComponent(parent)}`);
      setPickerFolders(data.folders || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao listar pastas"); }
    finally { setPickerLoading(false); }
  };
  const enterFolder = async (f) => {
    setPickerBreadcrumbs(prev => [...prev, { id: f.id, name: f.name }]);
    setPickerParent(f.id); await loadFolders(f.id);
  };
  const goBreadcrumb = async (idx) => {
    const next = pickerBreadcrumbs.slice(0, idx + 1);
    setPickerBreadcrumbs(next);
    const target = next[next.length - 1];
    setPickerParent(target.id); await loadFolders(target.id);
  };
  const selectFolder = async (f) => {
    const path = [...pickerBreadcrumbs, { id: f.id, name: f.name }].map(x => x.name).join(" / ");
    try {
      await api.put("/admin/drive/folder", { folder_id: f.id, folder_name: f.name, folder_path: path });
      toast.success(`Pasta selecionada: ${f.name}`); setPickerOpen(false); await fetchDrive();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar pasta"); }
  };
  const useCurrentFolder = async () => {
    const current = pickerBreadcrumbs[pickerBreadcrumbs.length - 1];
    if (current.id === "root") return toast.error("Selecione uma pasta específica (não a raiz)");
    const path = pickerBreadcrumbs.map(x => x.name).join(" / ");
    try {
      await api.put("/admin/drive/folder", { folder_id: current.id, folder_name: current.name, folder_path: path });
      toast.success(`Pasta selecionada: ${current.name}`); setPickerOpen(false); await fetchDrive();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar pasta"); }
  };
  const uploadLatest = async () => {
    setBusyUpload(true);
    try { const { data } = await api.post("/admin/drive/upload-latest"); toast.success(`Backup enviado: ${data.name}`); await fetchDrive(); await fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha no upload"); }
    finally { setBusyUpload(false); }
  };

  const fetchAll = async () => {
    try {
      const [s, a, au, px, bk] = await Promise.all([
        api.get("/security/summary"),
        api.get(`/security/login-attempts?filter=${attemptsFilter}&limit=200`),
        api.get("/security/audit?limit=200"),
        api.get("/security/pix-log?limit=300"),
        api.get("/admin/backup/list"),
      ]);
      setSummary(s.data); setAttempts(a.data); setAudit(au.data); setPix(px.data); setBackups(bk.data);
    } catch (e) { /* ignora */ }
  };

  useEffect(() => { fetchAll(); fetchConfig(); fetchDrive(); }, [attemptsFilter]);
  useEffect(() => {
    // Recebe mensagem do popup OAuth quando usuário autoriza
    const handler = (e) => {
      if (e.data && e.data.type === "drive-oauth") {
        if (e.data.ok) { toast.success("Google Drive conectado!"); fetchDrive(); }
        else { toast.error("Falha ao conectar Google Drive"); }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);
  useEffect(() => {
    pollRef.current = setInterval(fetchAll, 10000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptsFilter]);

  const createBackup = async () => {
    setBusyBackup(true);
    try { await api.post("/admin/backup/create"); toast.success("Backup gerado com sucesso"); await fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao gerar backup"); }
    finally { setBusyBackup(false); }
  };
  const downloadBackup = async (filename) => {
    try {
      const resp = await api.get(`/admin/backup/download/${filename}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Backup baixado — guarde em pendrive/HD externo");
    } catch (e) { toast.error("Falha no download"); }
  };
  const confirmRestore = async () => {
    try {
      await api.post("/admin/backup/restore", { filename: restoring, confirm: confirmText });
      toast.success("Restauração concluída!");
      setRestoring(null); setConfirmText(""); await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao restaurar"); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="security-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black uppercase flex items-center gap-3"><Shield className="w-8 h-8 text-rose-600"/>Segurança</h1>
          <p className="text-sm text-slate-500 mt-1">Monitoramento em tempo real · atualiza a cada 10s</p>
        </div>
        <Button variant="outline" onClick={fetchAll} data-testid="refresh-btn"><RefreshCcw className="w-4 h-4 mr-2"/>Atualizar</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryCard label="Falhas 24h" value={summary?.failed_24h} icon={AlertTriangle} tone="rose" testid="sum-failed24"/>
        <SummaryCard label="Falhas 7d" value={summary?.failed_7d} icon={AlertTriangle} tone="amber" testid="sum-failed7"/>
        <SummaryCard label="Bloqueios 7d" value={summary?.locked_7d} icon={Lock} tone="rose" testid="sum-locked"/>
        <SummaryCard label="Pix 30d" value={summary?.pix_30d} icon={FileWarning} tone="sky" testid="sum-pix"/>
        <SummaryCard label="Audit 7d" value={summary?.audit_7d} icon={Shield} tone="slate" testid="sum-audit"/>
        <SummaryCard label="Backups" value={summary?.backup_files} icon={Database} tone="emerald" testid="sum-backup"/>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="summary" data-testid="tab-attempts">Tentativas de login</TabsTrigger>
          <TabsTrigger value="pix" data-testid="tab-pix">Pix suspeitos</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">Backup</TabsTrigger>
          <TabsTrigger value="restore" data-testid="tab-restore">Restaurar</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Filtro:</span>
              {["failed","ok","all"].map(f=>(
                <Button key={f} size="sm" variant={attemptsFilter===f?"default":"outline"} onClick={()=>setAttemptsFilter(f)} data-testid={`filter-${f}`}>{f==="failed"?"Falhas":f==="ok"?"Sucesso":"Todas"}</Button>
              ))}
            </div>
            <div className="overflow-auto max-h-[520px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs uppercase text-slate-500 border-b">
                    <th className="py-2 pr-3">Quando</th><th className="pr-3">IP</th><th className="pr-3">Email/CPF</th><th className="pr-3">Origem</th><th className="pr-3">Status</th><th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((a,i)=>(
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 pr-3 text-slate-600">{fmtDate(a.at)}</td>
                      <td className="pr-3 font-mono text-xs">{a.ip}</td>
                      <td className="pr-3">{a.email}</td>
                      <td className="pr-3"><Badge variant="outline">{a.source||"web"}</Badge></td>
                      <td className="pr-3">{a.ok ? <Badge className="bg-emerald-600">OK</Badge> : <Badge className="bg-rose-600">FALHA</Badge>}</td>
                      <td className="text-xs text-slate-500">{a.reason||"—"}</td>
                    </tr>
                  ))}
                  {!attempts.length && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Nenhum registro.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="pix" className="mt-4">
          <Card className="p-4 mb-4">
            <h3 className="font-bold uppercase text-sm text-rose-600 mb-2">IPs suspeitos (muitas cobranças Pix)</h3>
            {pix.suspects?.length ? (
              <ul className="text-sm space-y-1">
                {pix.suspects.map((s,i)=>(
                  <li key={i} className="flex justify-between border-b py-1">
                    <span className="font-mono">{s.ip}</span>
                    <span><Badge className="bg-rose-600">{s.count} cobranças</Badge> <span className="text-slate-500 text-xs ml-2">{fmtDate(s.last_at)}</span></span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-slate-400 text-sm">Nenhum IP suspeito.</p>}
          </Card>
          <Card className="p-4">
            <h3 className="font-bold uppercase text-sm text-slate-600 mb-2">Últimas cobranças Pix (todas)</h3>
            <div className="max-h-[400px] overflow-auto text-sm">
              {pix.items?.slice(0,100).map((it,i)=>(
                <div key={i} className="flex justify-between border-b py-1">
                  <span className="font-mono text-xs">{it.ip}</span>
                  <span className="text-slate-500 text-xs">{fmtDate(it.at)}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Card className="p-4 overflow-auto max-h-[600px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="text-left text-xs uppercase text-slate-500 border-b">
                <th className="py-2 pr-3">Quando</th><th className="pr-3">Evento</th><th className="pr-3">IP</th><th className="pr-3">Path</th><th>Meta</th>
              </tr></thead>
              <tbody>
                {audit.map((a,i)=>(
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-3 text-slate-600">{fmtDate(a.at)}</td>
                    <td className="pr-3"><Badge variant="outline">{a.event}</Badge></td>
                    <td className="pr-3 font-mono text-xs">{a.ip}</td>
                    <td className="pr-3 text-xs">{a.path}</td>
                    <td className="text-xs text-slate-500 max-w-[300px] truncate">{JSON.stringify(a.meta||{})}</td>
                  </tr>
                ))}
                {!audit.length && <tr><td colSpan={5} className="py-6 text-center text-slate-400">Nenhum evento.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="backup" className="mt-4">
          {/* GOOGLE DRIVE */}
          <Card className="p-4 border-blue-300 bg-blue-50 mb-4">
            <div className="flex items-start gap-3 mb-3">
              {drive?.connected ? <Cloud className="w-5 h-5 text-blue-700"/> : <CloudOff className="w-5 h-5 text-slate-500"/>}
              <div className="flex-1">
                <h3 className="font-bold uppercase text-sm text-blue-900 flex items-center gap-2">Google Drive — destino do backup
                  {drive?.connected && <Badge className="bg-emerald-600">conectado</Badge>}
                </h3>
                <p className="text-xs text-slate-600 mt-1">
                  {!drive?.credentials_configured
                    ? <>⚠️ Credenciais do Google Cloud ainda não configuradas. <strong>PRECISA DE CONFIGURAÇÃO EXTERNA</strong>: crie um OAuth Client no Google Cloud Console e preencha <code className="font-mono bg-white px-1">GOOGLE_CLIENT_ID</code> e <code className="font-mono bg-white px-1">GOOGLE_CLIENT_SECRET</code> em <code className="font-mono bg-white px-1">/app/backend/.env</code>, depois reinicie o backend. URL de redirect: <code className="font-mono bg-white px-1 break-all">{drive?.redirect_uri}</code></>
                    : drive?.connected
                      ? <>Conectado como <strong>{drive.account_email}</strong>{drive.folder_name && <> · Pasta: <strong>{drive.folder_path || drive.folder_name}</strong></>}</>
                      : "Clique em 'Conectar' para autorizar o backup diário no seu Google Drive."}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!drive?.connected ? (
                <Button onClick={connectDrive} disabled={!drive?.credentials_configured} className="bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase" data-testid="drive-connect-btn">
                  <Cloud className="w-4 h-4 mr-2"/>Conectar Google Drive
                </Button>
              ) : (
                <>
                  <Button onClick={openPicker} className="bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase" data-testid="drive-pick-folder-btn">
                    <FolderOpen className="w-4 h-4 mr-2"/>{drive.folder_id ? "Trocar pasta" : "Selecionar pasta"}
                  </Button>
                  {drive.folder_id && (
                    <Button onClick={uploadLatest} disabled={busyUpload} variant="outline" className="border-blue-300 text-blue-800" data-testid="drive-upload-now-btn">
                      <Upload className="w-4 h-4 mr-2"/>{busyUpload ? "Enviando..." : "Enviar último backup agora"}
                    </Button>
                  )}
                  <Button onClick={disconnectDrive} variant="outline" className="border-rose-300 text-rose-700" data-testid="drive-disconnect-btn">
                    <CloudOff className="w-4 h-4 mr-2"/>Desconectar
                  </Button>
                </>
              )}
            </div>
            {drive?.last_upload_at && (
              <p className="text-[11px] mt-2 text-slate-600">
                Último upload: {new Date(drive.last_upload_at).toLocaleString("pt-BR")} · {drive.last_upload_file}
                {drive.last_upload_error && <span className="text-rose-600"> · erro: {drive.last_upload_error}</span>}
              </p>
            )}
          </Card>

          {/* PASTA NO SEU COMPUTADOR */}
          <Card className="p-4 border-emerald-300 bg-emerald-50 mb-4">
            <div className="flex items-start gap-3 mb-3">
              <HardDrive className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5"/>
              <div className="flex-1">
                <h3 className="font-bold uppercase text-sm text-emerald-900">Pasta no SEU computador</h3>
                <p className="text-xs text-slate-600 mt-1">Grava uma cópia do backup criptografado direto numa pasta do seu computador (pendrive, HD externo, pasta local). Precisa de Chrome, Edge ou Opera. Mantenha esta aba aberta para o auto-save funcionar.</p>
              </div>
              {localFolderName
                ? <Badge className="bg-emerald-600" data-testid="local-folder-badge">conectada: {localFolderName}</Badge>
                : <Badge variant="outline">não configurada</Badge>}
            </div>
            {!fileSystemAccessSupported() ? (
              <p className="text-sm text-rose-700 bg-white border border-rose-200 rounded p-2">Seu navegador não suporta seleção de pasta local. Use Chrome, Edge ou Opera na versão mais recente.</p>
            ) : isInIframe() ? (
              <div className="text-sm bg-white border border-amber-300 rounded p-3 space-y-2">
                <p className="text-amber-800"><strong>⚠️ A seleção de pasta precisa ser feita fora do preview do Emergent.</strong> O navegador bloqueia o seletor de arquivos dentro de iframes por segurança.</p>
                <Button onClick={()=>window.open(window.location.href, "_blank")} className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase" data-testid="local-open-new-tab">
                  <FolderOpen className="w-4 h-4 mr-2"/>Abrir painel em nova aba
                </Button>
                <p className="text-[11px] text-slate-500">Na nova aba você poderá escolher a pasta normalmente. Depois volte aqui — a pasta fica salva no navegador.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Button onClick={chooseLocalFolder} className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase" data-testid="local-pick-btn">
                    <FolderOpen className="w-4 h-4 mr-2"/>{localFolderName ? "Trocar pasta" : "Escolher pasta no computador"}
                  </Button>
                  {localFolderName && (
                    <>
                      <Button onClick={saveNowLocal} variant="outline" className="border-emerald-300" data-testid="local-save-now-btn"><Download className="w-4 h-4 mr-2"/>Salvar último agora</Button>
                      <label className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-emerald-300 rounded cursor-pointer" data-testid="local-autosave-toggle">
                        <input type="checkbox" checked={localAutoSave} onChange={e=>toggleAutoSave(e.target.checked)} />
                        <span>Auto-save (grava toda vez que o backend criar um novo)</span>
                      </label>
                      <Button onClick={forgetLocal} variant="outline" className="border-rose-300 text-rose-700" data-testid="local-forget-btn"><X className="w-4 h-4 mr-2"/>Esquecer</Button>
                    </>
                  )}
                </div>
                {localLastSaved && (
                  <p className="text-[11px] text-slate-600">Última gravação local: {new Date(localLastSaved).toLocaleString("pt-BR")}</p>
                )}
                <p className="text-[11px] text-slate-500">Dica: aponte para uma pasta no seu HD/pendrive. Como o navegador grava, o auto-save precisa que esta aba esteja aberta.</p>
              </div>
            )}
          </Card>

          <Card className="p-4 border-sky-300 bg-sky-50 mb-4">
            <div className="flex items-start gap-3 mb-3">
              <FolderCog className="w-5 h-5 text-sky-700 shrink-0 mt-0.5"/>
              <div className="flex-1">
                <h3 className="font-bold uppercase text-sm text-sky-900">Caminho no servidor (fallback)</h3>
                <p className="text-xs text-slate-600 mt-1">Pasta local do servidor. O backup <strong>sempre</strong> grava aqui primeiro — se Google Drive ou computador local estiverem conectados, a cópia é propagada para lá.</p>
              </div>
              {config && (
                config.writable
                  ? <span className="text-xs font-bold flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4"/>Gravável</span>
                  : <span className="text-xs font-bold flex items-center gap-1 text-rose-700"><XCircle className="w-4 h-4"/>Não gravável</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr,160px,auto] gap-2">
              <Input
                value={configForm.backup_path}
                onChange={e => setConfigForm(v => ({ ...v, backup_path: e.target.value }))}
                placeholder="/app/backups"
                data-testid="backup-path-input"
                className="font-mono text-sm"
              />
              <Input
                type="number" min="1" max="365"
                value={configForm.retention_days}
                onChange={e => setConfigForm(v => ({ ...v, retention_days: parseInt(e.target.value) || 30 }))}
                placeholder="dias"
                data-testid="backup-retention-input"
              />
              <Button onClick={saveConfig} disabled={busyConfig} className="bg-sky-700 hover:bg-sky-800 text-white font-bold uppercase" data-testid="save-backup-config-btn">
                <Save className="w-4 h-4 mr-2"/>{busyConfig ? "Salvando..." : "Salvar"}
              </Button>
            </div>
            {config?.last_config_saved_at && (
              <p className="text-[11px] text-slate-500 mt-2">Última alteração: {new Date(config.last_config_saved_at).toLocaleString("pt-BR")} por {config.last_config_saved_by || "—"}</p>
            )}
          </Card>

          {/* HORÁRIOS DO BACKUP */}
          <Card className="p-4 border-indigo-300 bg-indigo-50 mb-4">
            <div className="flex items-start gap-3 mb-3">
              <Clock className="w-5 h-5 text-indigo-700 shrink-0 mt-0.5"/>
              <div className="flex-1">
                <h3 className="font-bold uppercase text-sm text-indigo-900">Horários do backup automático</h3>
                <p className="text-xs text-slate-600 mt-1">Informe um ou mais horários do dia (fuso configurável). O cron do servidor roda a cada 15 min e dispara o backup dentro de uma janela de ±7 min do horário escolhido. Vazio = usa 04:00 BRT como padrão.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Input type="time" value={newTime} onChange={e=>setNewTime(e.target.value)} className="w-32" data-testid="sched-new-time"/>
              <Button onClick={addTime} className="bg-indigo-700 hover:bg-indigo-800 text-white uppercase font-bold" data-testid="sched-add-btn"><Plus className="w-4 h-4 mr-1"/>Adicionar horário</Button>
              <span className="text-xs text-slate-500 ml-2">Fuso: UTC
                <input type="number" value={tzOffset} onChange={e=>setTzOffset(parseInt(e.target.value)||0)} min="-12" max="14" className="ml-1 w-14 border rounded px-1 text-xs" data-testid="sched-tz"/>
              </span>
            </div>
            <div className="flex flex-wrap gap-2 min-h-[32px]">
              {schedTimes.length === 0 && <span className="text-xs text-slate-400">Nenhum horário configurado — usará padrão 04:00 BRT.</span>}
              {schedTimes.map(t => (
                <span key={t} className="inline-flex items-center gap-1 bg-white border border-indigo-200 rounded px-2 py-1 text-sm" data-testid={`sched-${t}`}>
                  <Clock className="w-3 h-3 text-indigo-600"/>{t}
                  <button onClick={()=>removeTime(t)} className="ml-1 text-rose-500 hover:text-rose-700"><X className="w-3 h-3"/></button>
                </span>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">Lembre: clique em <strong>Salvar</strong> na seção "Caminho no servidor" acima para persistir os horários no banco.</p>
          </Card>

          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold uppercase text-sm text-slate-700">Backups ({backups.files?.length || 0})</h3>
            <Button onClick={createBackup} disabled={busyBackup} className="bg-slate-900 hover:bg-black text-white uppercase font-bold" data-testid="create-backup-btn">
              <Database className="w-4 h-4 mr-2"/>{busyBackup ? "Gerando..." : "Gerar backup agora"}
            </Button>
          </div>

          <Card className="p-4 overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
                <th className="py-2 pr-3">Arquivo</th><th className="pr-3">Tamanho</th><th className="pr-3">Criado em</th><th className="text-right">Ações</th>
              </tr></thead>
              <tbody>
                {backups.files?.map((f, i)=>(
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-mono text-xs">{f.filename}</td>
                    <td className="pr-3">{fmtSize(f.size)}</td>
                    <td className="pr-3 text-slate-600">{fmtDate(f.created_at)}</td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" onClick={()=>downloadBackup(f.filename)} data-testid={`dl-${i}`}><Download className="w-3.5 h-3.5 mr-1"/>Baixar</Button>
                      <Button size="sm" variant="outline" className="ml-2 border-rose-300 text-rose-700" onClick={()=>{setRestoring(f.filename); setConfirmText("");}} data-testid={`restore-${i}`}><Trash2 className="w-3.5 h-3.5 mr-1"/>Restaurar</Button>
                    </td>
                  </tr>
                ))}
                {!backups.files?.length && <tr><td colSpan={4} className="py-6 text-center text-slate-400">Sem backups ainda — clique em "Gerar backup agora".</td></tr>}
              </tbody>
            </table>
          </Card>

          {backups.log?.length ? (
            <Card className="p-4 mt-4">
              <h4 className="font-bold uppercase text-xs text-slate-500 mb-2">Log de eventos de backup</h4>
              <ul className="text-xs space-y-1 max-h-[200px] overflow-auto">
                {backups.log.map((l,i)=>(
                  <li key={i} className="flex justify-between border-b py-1">
                    <span>{l.action||l.trigger} · {l.filename}</span>
                    <span className="text-slate-500">{l.by_name||l.created_by_name||"system"} · {fmtDate(l.at||l.created_at)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </TabsContent>

        <TabsContent value="restore" className="mt-4" data-testid="restore-tab-content">
          <Card className="p-4 border-rose-300 bg-rose-50 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-700 shrink-0 mt-0.5"/>
              <div className="text-sm text-rose-800">
                <strong>⚠️ Ação destrutiva.</strong> Restaurar um backup <strong>apaga TODOS</strong> os dados atuais (alunos, planos, financeiro, treinos, logs) e substitui pelos dados do backup selecionado. Use somente em caso real de perda de dados ou retorno de versão. Sempre gere um backup atual antes de restaurar.
              </div>
            </div>
          </Card>

          <Card className="p-4 mb-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="font-bold uppercase text-sm text-slate-700">1. Enviar arquivo .tar.enc do seu computador</h3>
                <p className="text-xs text-slate-500 mt-1">Use essa opção se você tem um backup antigo no pendrive, HD externo ou Google Drive. O arquivo precisa ter sido gerado por este sistema (criptografia Fernet).</p>
              </div>
              <input ref={uploadRef} type="file" accept=".enc,.tar.enc" className="hidden" onChange={onUploadFile} data-testid="restore-upload-input"/>
              <Button onClick={onPickUpload} disabled={uploading} className="bg-slate-900 hover:bg-black text-white font-bold uppercase shrink-0" data-testid="restore-upload-btn">
                <Upload className="w-4 h-4 mr-2"/>{uploading ? "Enviando..." : "Enviar backup"}
              </Button>
            </div>
          </Card>

          <Card className="p-4 mb-4">
            <h3 className="font-bold uppercase text-sm text-slate-700 mb-3">2. Escolher backup para restaurar ({backups.files?.length || 0} disponíveis)</h3>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
                  <th className="py-2 pr-3">Arquivo</th><th className="pr-3">Tamanho</th><th className="pr-3">Criado em</th><th className="text-right">Ação</th>
                </tr></thead>
                <tbody>
                  {backups.files?.map((f, i)=>(
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 pr-3 font-mono text-xs">{f.filename}</td>
                      <td className="pr-3">{fmtSize(f.size)}</td>
                      <td className="pr-3 text-slate-600">{fmtDate(f.created_at)}</td>
                      <td className="text-right">
                        <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white font-bold" onClick={()=>{setRestoring(f.filename); setConfirmText("");}} data-testid={`restore-pick-${i}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1"/>Restaurar este
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!backups.files?.length && <tr><td colSpan={4} className="py-6 text-center text-slate-400">Nenhum backup disponível. Envie um arquivo acima ou gere um na aba Backup.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="font-bold uppercase text-sm text-slate-700 mb-3">Histórico de restaurações e uploads</h3>
            <div className="max-h-[300px] overflow-auto">
              {restoreHistory.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nenhum evento registrado ainda.</p>
              ) : (
                <ul className="text-xs space-y-1">
                  {restoreHistory.map((h,i)=>(
                    <li key={i} className="flex justify-between border-b py-1">
                      <span>
                        <Badge className={h.action==="restore" ? "bg-rose-600" : "bg-slate-600"} data-testid={`history-${h.action}-${i}`}>{h.action}</Badge>
                        <span className="ml-2 font-mono">{h.filename}</span>
                        {h.ok === false && <span className="text-rose-600 ml-2">· falhou</span>}
                      </span>
                      <span className="text-slate-500">{h.by_name||"system"} · {fmtDate(h.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Selecionar pasta no Google Drive</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-1 flex-wrap text-sm">
              {pickerBreadcrumbs.map((b, i) => (
                <span key={b.id} className="flex items-center gap-1">
                  {i > 0 && <span className="text-slate-400">/</span>}
                  <button onClick={()=>goBreadcrumb(i)} className="hover:underline font-medium text-blue-700">{b.name}</button>
                </span>
              ))}
            </div>
            <div className="border rounded max-h-[400px] overflow-auto" data-testid="drive-picker-list">
              {pickerLoading ? (
                <div className="p-6 text-center text-slate-400 text-sm">Carregando...</div>
              ) : pickerFolders.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">Nenhuma subpasta aqui.</div>
              ) : (
                <ul>
                  {pickerFolders.map(f => (
                    <li key={f.id} className="flex items-center justify-between border-b last:border-0 px-3 py-2 hover:bg-slate-50">
                      <button onClick={()=>enterFolder(f)} className="flex items-center gap-2 text-left flex-1" data-testid={`drive-folder-${f.id}`}>
                        <FolderOpen className="w-4 h-4 text-blue-600"/>
                        <span className="text-sm">{f.name}</span>
                      </button>
                      <Button size="sm" onClick={()=>selectFolder(f)} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`drive-select-${f.id}`}>Usar esta</Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPickerOpen(false)}>Cancelar</Button>
            {pickerBreadcrumbs.length > 1 && (
              <Button onClick={useCurrentFolder} className="bg-emerald-600 hover:bg-emerald-700" data-testid="drive-use-current-btn">
                <CheckCircle2 className="w-4 h-4 mr-2"/>Usar "{pickerBreadcrumbs[pickerBreadcrumbs.length-1].name}"
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!restoring} onOpenChange={(v)=>!v && setRestoring(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-rose-700">Restaurar banco de dados</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-rose-700 font-bold">⚠️ Ação destrutiva — vai sobrescrever TODOS os dados atuais com o backup selecionado:</p>
            <code className="bg-slate-100 p-2 block text-xs break-all">{restoring}</code>
            <p>Para confirmar, digite <strong>RESTAURAR</strong> abaixo:</p>
            <Input value={confirmText} onChange={e=>setConfirmText(e.target.value)} placeholder="RESTAURAR" data-testid="restore-confirm"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setRestoring(null)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={confirmRestore} disabled={confirmText!=="RESTAURAR"} data-testid="restore-confirm-btn">Restaurar agora</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
